import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Briefcase, BookOpen, Users, Clock, ChevronRight, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

export default function Formateurs() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [formateurs, setFormateurs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        if (!["responsable", "admin"].includes(user.role)) { navigate("/dashboard"); return; }
        (async () => {
            try {
                const { data } = await api.get("/responsable/formateurs");
                setFormateurs(data || []);
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
            finally { setLoading(false); }
        })();
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="formateurs-page">
                <div className="label-eyebrow text-orange mb-2">Espace responsable de formation</div>
                <h1 className="h-display text-3xl sm:text-4xl font-semibold leading-tight mb-2">
                    Mes <span className="text-orange">formateurs</span>
                </h1>
                <p className="text-sm text-muted2 mb-8 max-w-2xl">
                    Liste des formateurs de votre établissement. Cliquez sur un formateur pour voir tous les cours qu'il a saisis et les évaluations de compétences faites à chaque alternant.
                </p>

                {loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm">
                        <Loader2 size={16} className="animate-spin mx-auto mb-2" /> Chargement…
                    </div>
                ) : formateurs.length === 0 ? (
                    <div className="panel p-10 text-center" data-testid="formateurs-empty">
                        <Briefcase size={32} className="text-muted2 mx-auto mb-3" strokeWidth={1.5} />
                        <h2 className="h-display text-lg font-semibold">Aucun formateur inscrit</h2>
                        <p className="text-xs text-muted2 mt-2 max-w-md mx-auto">
                            Partagez votre code d'établissement <span className="font-mono text-orange">{user?.invite_code}</span> à vos formateurs pour qu'ils créent leur compte gratuit.
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {formateurs.map((f) => (
                            <Link
                                key={f.id}
                                to={`/formateurs/${f.id}`}
                                className="panel p-5 hover:border-orange transition-colors group"
                                data-testid={`formateur-card-${f.id}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="h-display text-lg font-semibold truncate">
                                            {f.prenom || ""} {f.nom || ""}
                                        </div>
                                        <div className="text-xs font-mono text-muted2 truncate flex items-center gap-1 mt-0.5">
                                            <Mail size={10} /> {f.email}
                                        </div>
                                        {f.phone && (
                                            <div className="text-xs font-mono text-muted2 flex items-center gap-1 mt-0.5">
                                                <Phone size={10} /> {f.phone}
                                            </div>
                                        )}
                                    </div>
                                    <ChevronRight size={18} className="text-muted2 group-hover:text-orange shrink-0" />
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-rule">
                                    <div>
                                        <div className="label-eyebrow !text-[9px]">Cours / TPs</div>
                                        <div className="h-display text-xl font-bold flex items-center gap-1.5 mt-0.5">
                                            <BookOpen size={14} className="text-orange" /> {f.courses_count}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="label-eyebrow !text-[9px]">Alternants évalués</div>
                                        <div className="h-display text-xl font-bold flex items-center gap-1.5 mt-0.5">
                                            <Users size={14} className="text-orange" /> {f.alternants_evaluated}
                                        </div>
                                    </div>
                                </div>
                                {f.last_activity_at && (
                                    <div className="text-[10px] font-mono text-muted2 mt-3 flex items-center gap-1">
                                        <Clock size={10} /> Dernière séance · {new Date(f.last_activity_at).toLocaleDateString("fr-FR")}
                                    </div>
                                )}
                            </Link>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
